/* eslint-disable react/no-unstable-nested-components */

import { useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query';
import {
	type ColumnDef,
	type ColumnFiltersState,
	flexRender,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	type SortingState,
	useReactTable,
	type VisibilityState,
} from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import ConfirmModal from '@/components/ConfirmModal/ConfirmModal';
import { DataTableColumnHeader } from '@/components/DataTable/DataTableColumnHeader';
import { DataTablePagination } from '@/components/DataTable/DataTablePagination';
import StatusCircle from '@/components/StatusCircle/StatusCircle';
import TableFilters from '@/components/TableFilters/TableFilters';
import { showErrorToast } from '@/components/ToastNotification';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import { SetStatus } from '@/Enums/enum';
import { massApproveSubmissions } from '@/hooks/mutations/submissionMutations';
import { useGetAllSubmissions } from '@/hooks/queries/useSubmissionsAll';
import { useHashScrollTo } from '@/hooks/useHashScrollTo';
import { usePermissions } from '@/hooks/usePermissions';
import { ApprovalsIcon, EditIcon, MinusIcon } from '@/icons/index';
import { supabase } from '@/lib/supabase';
import type { CourseWithRelations, SubmissionWithCourse } from '@/lib/types';
import { StyledH4 } from '@/Shared/Typography/typography';

const Submissions = () => {
	const { isAdmin, canApproveSubmissions } = usePermissions();
	const [allSubmissions, setSubmissions] = useState<SubmissionWithCourse[] | null>();
	const { data: submissionData, isLoading, isFetched } = useGetAllSubmissions();

	const [isConfirming, setIsConfirming] = useState(false);
	// scroll to course if hash is set in url
	useHashScrollTo();

	useEffect(() => {
		const abortController = new AbortController();
		setSubmissions(submissionData ?? null);

		return () => {
			abortController.abort();
		};
	}, [submissionData]);

	const updateCourseSubId = async (variables: { [key: string]: string }) => {
		const { courseId, subId } = variables;

		// Update course to remove submission_id
		const { error: courseError } = await supabase
			.from('courses')
			.update({ submission_id: null })
			.eq('course_id', courseId);

		if (courseError) throw courseError;

		// Delete the submission
		const { error: subError } = await supabase
			.from('submissions')
			.delete()
			.eq('submission_id', subId);

		if (subError) throw subError;

		return { affected_rows: 1 };
	};

	const queryClient = useQueryClient();
	const { mutate: courseIdMutate } = useMutation({
		mutationFn: updateCourseSubId,
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['get-all-subs'],
			});
		},
		onError: () => {
			showErrorToast('Oh no, something went wrong... Please try again.');
		},
	});

	const dismissRow = useCallback(
		async (subId: string, courseId: string) => {
			courseIdMutate({ courseId, subId });
		},
		[courseIdMutate]
	);

	const querySubmissionData = useMemo(
		() =>
			allSubmissions &&
			allSubmissions?.map((submission) => ({
				courseColumn: submission?.course?.course_name,
				teacherColumn: submission,
				statusColumn: {
					adminApproval: submission.admin_approval,
					deptApproval: submission.dept_approval,
				},
				actionsColumn: submission,
				idColumn: submission.submission_id,
			})),
		[allSubmissions]
	);

	const [filteredData, setFilteredData] = useState<SubmissionTableData[]>([]);
	const [sorting, setSorting] = useState<SortingState>([]);
	const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
	const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

	useEffect(() => {
		if (querySubmissionData) {
			setFilteredData(querySubmissionData as SubmissionTableData[]);
		}
	}, [querySubmissionData]);

	const handleSetData = useCallback((submissions: any) => {
		setFilteredData(submissions as SubmissionTableData[]);
	}, []);

	type SubmissionTableData = {
		courseColumn: string;
		teacherColumn: SubmissionWithCourse;
		statusColumn: {
			adminApproval: string;
			deptApproval: string;
		};
		actionsColumn: SubmissionWithCourse;
		idColumn: string;
	};

	const columns = useMemo<ColumnDef<SubmissionTableData>[]>(
		() => [
			{
				accessorKey: 'courseColumn',
				header: ({ column }) => <DataTableColumnHeader column={column} title="Course" />,
				cell: ({ row }) => <div>{row.getValue('courseColumn')}</div>,
			},
			{
				accessorKey: 'teacherColumn',
				header: ({ column }) => <DataTableColumnHeader column={column} title="Teacher" />,
				cell: ({ row }) => {
					const course = row.original.actionsColumn.course;
					if (!course) return <div />;
					const courseUsers = course.courses_users;

					if (courseUsers && courseUsers.length !== 0) {
						return (
							<div className="flex flex-col gap-1">
								{(courseUsers as NonNullable<CourseWithRelations['courses_users']>).map(
									(courseUser) => {
										const { user } = courseUser;
										return (
											<div key={`${courseUser.course_id}-${courseUser.user_id}`}>
												{`${user?.user_first ?? ''} ${user?.user_last ?? ''}`}
											</div>
										);
									}
								)}
							</div>
						);
					}
					return <div />;
				},
				enableSorting: false,
			},
			{
				accessorKey: 'statusColumn',
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Status" className="text-center" />
				),
				cell: ({ row }) => {
					const value = row.getValue('statusColumn') as {
						adminApproval: string;
						deptApproval: string;
					};
					return (
						<div className="flex justify-center">
							<StatusCircle
								approvalOne={value.deptApproval as any}
								approvalTwo={value.adminApproval as any}
							/>
						</div>
					);
				},
				enableSorting: false,
			},
			{
				id: 'actions',
				enableHiding: false,
				cell: ({ row }) => {
					const { deptApproval, adminApproval } = row.original.statusColumn;
					const { course, submission_id: submissionId } = row.original.actionsColumn;
					if (!course) return <div />;
					const courseId = course.course_id;

					const isDismissible = [deptApproval, adminApproval].every(
						(status: string) => status === SetStatus.APPROVED
					);

					return (
						<div className="flex justify-center [&>*:not(:last-child)]:mr-[var(--spacing-4)]">
							{canApproveSubmissions && (
								<Link to={`${courseId}?isApprovalMode=true`}>
									<ApprovalsIcon />
								</Link>
							)}
							<Link to={`${courseId}`}>
								<EditIcon />
							</Link>
							{(isDismissible || isAdmin) && (
								<MinusIcon dismissRow={() => dismissRow(submissionId, courseId)} />
							)}
						</div>
					);
				},
			},
		],
		[dismissRow, canApproveSubmissions, isAdmin]
	);

	const table = useReactTable({
		data: filteredData,
		columns,
		onSortingChange: setSorting,
		onColumnFiltersChange: setColumnFilters,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		onColumnVisibilityChange: setColumnVisibility,
		state: {
			sorting,
			columnFilters,
			columnVisibility,
		},
		initialState: {
			pagination: {
				pageSize: 10,
			},
		},
	});

	const isSubmittingPosts = useIsMutating({ mutationKey: ['mass-approve-submissions'] });

	useEffect(() => {
		if (isSubmittingPosts === 0) return;

		const handleBeforeUnload = (e: BeforeUnloadEvent) => {
			e.preventDefault();
		};

		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => window.removeEventListener('beforeunload', handleBeforeUnload);
	}, [isSubmittingPosts]);

	const { mutate: massApproveMutation } = useMutation({
		mutationFn: () => massApproveSubmissions(submissionData ?? []),
		mutationKey: ['mass-approve-submissions'],
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['get-all-subs'],
			});
		},
		onError: () => {
			showErrorToast('All submissions not approved... Please try again.');
		},
	});

	const canApproveAll = () => {
		if (submissionData?.length === 0) {
			return true;
		}

		const allApproved = submissionData?.every(
			(submission) =>
				submission.admin_approval === SetStatus.APPROVED &&
				submission.dept_approval === SetStatus.APPROVED
		);

		return allApproved ?? false;
	};

	return (
		<>
			{isAdmin && (
				<Button
					className="absolute z-10"
					size="small"
					disabled={isSubmittingPosts !== 0 || canApproveAll()}
					onClick={() => setIsConfirming(true)}
				>
					Approve All Submissions
				</Button>
			)}
			{isConfirming && (
				<ConfirmModal
					modalActive={isConfirming}
					onConfirm={massApproveMutation}
					onCancel={() => setIsConfirming(false)}
					triggerModal={() => setIsConfirming(false)}
					selectedData="This process could take some time..."
					message="Are you sure you want to approve all submissions?"
				/>
			)}
			{querySubmissionData && (
				<TableFilters
					data={querySubmissionData}
					handleSetData={handleSetData}
					searchColumn={['courseColumn', 'teacherColumn', 'statusColumn']}
					filteredColumn="statusColumn"
					dropdown={false}
					resultQty={filteredData?.length ?? 0}
				/>
			)}
			{isLoading && (
				<div className="flex flex-col pb-5 pt-5 h-full">
					<Skeleton className="w-full mb-3 h-24" />
					<Skeleton className="w-full mb-3 h-24" />
					<Skeleton className="w-full mb-3 h-24" />
					<Skeleton className="w-full mb-3 h-24" />
					<Skeleton className="w-full mb-3 h-24" />
					<Skeleton className="w-full mb-3 h-24" />
				</div>
			)}
			{isFetched && !isLoading && (
				<div className="flex-1 flex flex-col min-h-0">
					<div
						className="flex-1 overflow-auto rounded-md"
						style={{ maxHeight: 'calc(100vh - 300px)' }}
					>
						<Table className="text-md typo-body-2">
							<TableHeader className="sticky top-0 bg-background z-10">
								{table.getHeaderGroups().map((headerGroup) => (
									<TableRow key={headerGroup.id}>
										{headerGroup.headers.map((header) => {
											return (
												<TableHead key={header.id}>
													{header.isPlaceholder
														? null
														: flexRender(header.column.columnDef.header, header.getContext())}
												</TableHead>
											);
										})}
									</TableRow>
								))}
							</TableHeader>
							<TableBody>
								{table.getRowModel().rows?.length ? (
									table.getRowModel().rows.map((row) => (
										<TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
											{row.getVisibleCells().map((cell) => (
												<TableCell key={cell.id}>
													{flexRender(cell.column.columnDef.cell, cell.getContext())}
												</TableCell>
											))}
										</TableRow>
									))
								) : (
									<TableRow>
										<TableCell colSpan={columns.length} className="h-24 text-center">
											No results.
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</div>
					<div className="mt-4 shrink-0 pb-4 border-t border-gray-200 pt-4">
						<DataTablePagination table={table} />
					</div>
				</div>
			)}
			{isSubmittingPosts !== 0 && (
				<div className="fixed inset-0 z-20 flex flex-col items-center justify-center w-full h-full p-6 bg-black/70 backdrop-blur-sm">
					<div className="flex flex-col items-center max-w-md text-center">
						<svg
							viewBox="0 0 50 165"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
							aria-hidden="true"
							className="mb-8 w-12 [&>path]:fill-transparent [&>path]:stroke-[0.5px] [&>path]:stroke-white"
						>
							<path
								className="animate-dash [stroke-dashoffset:10px] [stroke-dasharray:350px]"
								d="M22 3c14.91 0 27 11.9 27 26.577v60.147c0 12.901-4.654 17.41-18.05 30.391-1.845 1.787-3.854 3.734-6.04 5.885V33.388l-.097-.482c-.069-.343-.216-.517-.38-.709a3.25 3.25 0 01-.202-.256c-.579-.835-2.231-.964-2.231-.964V3z"
								fill="#03C5AF"
							/>
							<path
								className="animate-dash [stroke-dashoffset:10px] [stroke-dasharray:350px]"
								d="M19.227 133.585v6.261l-.677-.581c-6.846-6.481-15.777-14.862-15.866-14.947a2.102 2.102 0 01-.015-3.09c.898-.858 2.358-.863 3.265-.014.083.077 6.735 6.17 13.293 12.371z"
								fill="#484969"
							/>
							<path
								className="animate-dash [stroke-dashoffset:10px] [stroke-dasharray:350px]"
								d="M19.227 159.599c0 1.326 1.285 2.401 2.871 2.401s2.871-1.075 2.871-2.401v-8.195c1.017-1.014 1.974-1.97 2.91-2.906l.007-.006c1.925-1.925 3.763-3.761 5.85-5.828a2.767 2.767 0 00.01-3.925 2.79 2.79 0 00-3.94-.011c-1.244 1.232-1.813 1.838-2.4 2.463a87.26 87.26 0 01-2.437 2.508v-2.279h.058a7494.793 7494.793 0 0118.788-18.627l.002-.001.358-.354a2.767 2.767 0 00.017-3.925 2.792 2.792 0 00-3.94-.017c-.1.097-7.368 7.215-15.283 15.089V33.546C24.97 32.22 23.684 31 22.1 31c-1.587 0-2.872 1.22-2.872 2.546v108.226c-1.017-.956-1.579-1.522-2.127-2.074-.559-.562-1.103-1.111-2.102-2.044a2.095 2.095 0 00-3.023.143 2.247 2.247 0 00.146 3.111c1.264 1.184 2.433 2.396 3.605 3.611 1.135 1.177 2.273 2.356 3.501 3.514v11.566z"
								fill="#484969"
							/>
							<path
								className="animate-dash [stroke-dashoffset:10px] [stroke-dasharray:350px]"
								d="M19.136 125V33.518c0-1.328 1.283-2.55 2.864-2.55V3c-5.273 0-9.546 6.45-9.546 14.404v16.661c-.614.46-1.253.978-1.947 1.554C4.782 40.362 1 48.551 1 57.86v31.357c0 13.091 3.251 17.155 13.932 30.506l.002.001c1.29 1.613 2.688 3.361 4.202 5.277z"
								fill="#9190B4"
							/>
						</svg>
						<StyledH4 className="text-white mb-3">Approving All Submissions...</StyledH4>
						<p className="text-white/70 text-md leading-relaxed">
							This may take a few moments. Please keep this tab open until the process completes.
						</p>
					</div>
				</div>
			)}
		</>
	);
};

export default Submissions;
